import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLoader } from 'components/loader/loader-context';
import { UseAlert } from 'hook/alert-hook';
import { UseAuth } from 'hook/auth-hook';
import { getEnv } from 'helper/window-helper';
import { showAlertPorStatus, showErrorAlert, showSuccessAlert } from 'helper/show-alert-helper';
import { isConflito, resolverMensagemErro, MENSAGEM_BLOQUEIO_PADRAO, geracaoLiberadaApos } from 'helper/fluxo-aprovacao-pedido-helper';
import { FluxoAprovacaoPedidoUseCase, ItemBusca, SolicitacaoItem } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import { ServiceOperationResult } from 'service/api/rest/rest-usecase';
import { PedidoDetalhe } from '../../../@types/entity/pedido-detalhe-entity';
import { FluxoAprovacaoPedidoEntity } from '../../../@types/entity/fluxo-aprovacao-pedido-entity';
import { FluxoAprovacaoPedidoStatus } from '../../../@types/enums/fluxo-aprovacao-pedido-status-enum';
import { FluxoAprovacaoPedidoOperadorPage } from 'pages/layout/analysis/fluxo-aprovacao-pedido-operador-page';
import ErrorNotAuthorizedPage from 'pages/layout/error/error-not-authorized-page';
import { hasAcessoFluxoAprovacaoPedido } from 'helper/role-helper';

type GeracaoAutomatica = 'ignorada' | 'gerada' | 'falhou';

export const FluxoAprovacaoPedidoOperadorFactory: React.FC = () => {
  const ENDPOINT = getEnv('REACT_APP_END_POINT_FLUXO_APROVACAO_PEDIDO');

  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = UseAlert();
  const { loggedUserData } = UseAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [showScreen, setShowScreen] = useState<boolean>(false);
  const [detalhe, setDetalhe] = useState<PedidoDetalhe>(() => resolveDetalheInicial(location.state, params.idPedido));

  const service = useMemo(() => new FluxoAprovacaoPedidoUseCase(ENDPOINT!), [ENDPOINT]);

  useEffect(() => {
    if (hasAcessoFluxoAprovacaoPedido(loggedUserData?.role)) {
      setShowScreen(true);
    }
  }, [loggedUserData]);

  const carregarDetalhe = useCallback(
    async (idPedido: number) => {
      if (!idPedido) return;
      showLoader();
      const result = await service.carregarDetalhe(idPedido);
      hideLoader();
      if (result.success && result.data) {
        setDetalhe(result.data);
        return;
      }
      showErrorAlert(showAlert, resolverMensagemErro(result, 'Não foi possível carregar o pedido.'));
    },
    [service, showLoader, hideLoader, showAlert]
  );

  useEffect(() => {
    if (showScreen) {
      carregarDetalhe(detalhe.idPedido);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScreen]);

  function tratarErro(result: ServiceOperationResult<unknown>, msgErro: string) {
    const fallback = isConflito(result.statusCode) ? MENSAGEM_BLOQUEIO_PADRAO : msgErro;
    showAlertPorStatus(showAlert, resolverMensagemErro(result, fallback), result.statusCode);
  }

  /** Após ação bem-sucedida, redireciona para a lista. */
  function voltarParaLista() {
    navigate('/fluxo-aprovacao-pedido');
  }

  /** Gera automaticamente o pedido quando a aprovação/validação liberou a geração. */
  async function gerarSeLiberado(entity?: FluxoAprovacaoPedidoEntity | null): Promise<GeracaoAutomatica> {
    if (!geracaoLiberadaApos(entity)) {
      return 'ignorada';
    }
    const result = await service.gerarPedido(entity!.idPedido);
    if (!result.success) {
      tratarErro(result, 'Pedido aprovado, mas não foi possível gerar automaticamente.');
      return 'falhou';
    }
    return 'gerada';
  }

  async function aprovarOuValidar(
    acao: Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>>,
    msgErro: string,
    msgOk: string
  ) {
    showLoader();
    const result = await acao;
    if (!result.success) {
      hideLoader();
      tratarErro(result, msgErro);
      return;
    }
    const geracao = await gerarSeLiberado(result.data);
    hideLoader();
    if (geracao === 'falhou') {
      voltarParaLista();
      return;
    }
    showSuccessAlert(showAlert, geracao === 'gerada' ? 'Pedido aprovado e gerado com sucesso.' : result.message || msgOk);
    voltarParaLista();
  }

  async function acaoSimples(
    acao: Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>>,
    msgErro: string,
    msgOk: string
  ) {
    showLoader();
    const result = await acao;
    hideLoader();
    if (!result.success) {
      tratarErro(result, msgErro);
      return;
    }
    showSuccessAlert(showAlert, result.message || msgOk);
    voltarParaLista();
  }

  const handleEnviar = (id: number) =>
    acaoSimples(service.enviarParaAprovacao(id), 'Não foi possível enviar o pedido.', 'Pedido enviado para aprovação com sucesso.');

  const handleAprovar = (id: number) =>
    aprovarOuValidar(service.aprovar(id), 'Não foi possível aprovar o pedido.', 'Pedido aprovado com sucesso.');

  const handleValidar = (id: number) =>
    // Para o usuário é "Aprovar" nos dois turnos; o endpoint segue sendo `validar`.
    aprovarOuValidar(service.validar(id), 'Não foi possível aprovar o pedido.', 'Pedido aprovado com sucesso.');

  const handleReprovar = (id: number, justificativa: string) =>
    acaoSimples(service.reprovar(id, justificativa), 'Não foi possível reprovar o pedido.', 'Pedido reprovado.');

  async function handleEditar(id: number, solicitacoes: SolicitacaoItem[], justificativa: string) {
    // Em elaboração (ex.: envio bloqueado por falta de aprovador) a edição gera direto uma nova versão;
    // nos demais status vira solicitação para a contraparte decidir.
    if (detalhe.status !== FluxoAprovacaoPedidoStatus.EM_ELABORACAO) {
      return acaoSimples(
        service.editarPedido(id, solicitacoes, justificativa),
        'Não foi possível enviar as solicitações.',
        'Solicitações enviadas para a contraparte.'
      );
    }
    showLoader();
    const result = await service.editarPedido(id, solicitacoes, justificativa);
    hideLoader();
    if (!result.success) {
      tratarErro(result, 'Não foi possível salvar a nova versão.');
      return;
    }
    showSuccessAlert(showAlert, result.message || 'Nova versão salva. O pedido já pode ser reenviado.');
    carregarDetalhe(id);
  }

  /** Decidir mantém o usuário na tela e recarrega o detalhe para refletir o novo snapshot (não passa a vez). */
  async function handleDecidir(id: number, idsAceitas: number[], idsRecusadas: number[]) {
    showLoader();
    const result = await service.decidirSolicitacoes(id, idsAceitas, idsRecusadas);
    hideLoader();
    if (!result.success) {
      tratarErro(result, 'Não foi possível decidir as solicitações.');
      return;
    }
    showSuccessAlert(showAlert, result.message || 'Solicitações processadas com sucesso.');
    carregarDetalhe(id);
  }

  /** Retirar mantém o usuário na tela e recarrega o detalhe (não passa a vez). */
  async function handleRetirar(id: number, idSolicitacao: number) {
    showLoader();
    const result = await service.retirarSolicitacao(id, idSolicitacao);
    hideLoader();
    if (!result.success) {
      tratarErro(result, 'Não foi possível retirar a solicitação.');
      return;
    }
    showSuccessAlert(showAlert, result.message || 'Solicitação retirada.');
    carregarDetalhe(id);
  }

  const handleGerar = (id: number) =>
    acaoSimples(service.gerarPedido(id), 'Não foi possível gerar o pedido.', 'Pedido gerado com sucesso.');

  /** Busca itens da filial do pedido (deep-link) para o typeahead da edição. */
  const buscarItensDisponiveis = useCallback(
    async (termo: string): Promise<ItemBusca[]> => {
      const result = await service.buscarItens(detalhe.idPedido, termo);
      return result.data ?? [];
    },
    [service, detalhe.idPedido]
  );

  return (
    <>
      {showScreen ? (
        <FluxoAprovacaoPedidoOperadorPage
          detalhe={detalhe}
          onEnviar={handleEnviar}
          onAprovar={handleAprovar}
          onValidar={handleValidar}
          onReprovar={handleReprovar}
          onEditar={handleEditar}
          onDecidir={handleDecidir}
          onRetirar={handleRetirar}
          onGerar={handleGerar}
          onBuscarItens={buscarItensDisponiveis}
          onVoltar={() => navigate('/fluxo-aprovacao-pedido')}
        />
      ) : (
        <ErrorNotAuthorizedPage />
      )}
    </>
  );
};

function resolveDetalheInicial(state: unknown, idParam?: string): PedidoDetalhe {
  const pedidoState = (state as { pedido?: { idPedido?: number; status?: FluxoAprovacaoPedidoStatus } } | null)?.pedido;
  const idBruto = pedidoState?.idPedido ?? Number(idParam);
  return {
    idPedido: Number(idBruto) || 0,
    status: pedidoState?.status ?? FluxoAprovacaoPedidoStatus.EM_ELABORACAO,
    itens: [],
  };
}

export default FluxoAprovacaoPedidoOperadorFactory;
